import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { fetchMyCalendlyOwners } from 'actions/workflows'
import { useGetPolicyQuery } from 'common'
import { EmptyState } from 'components/empty_state'
import { isRestrictedFeatureSelector } from 'features/billing'
import { useNewEmptyStates } from 'features/empty_state'
import { useQueryParams } from 'hooks/use_query_params'
import { idSelector, isAdminOrOwnerSelector } from 'selectors/current_user'
import { ownerListLoadedSelector } from 'selectors/workflows'
import { getTemplatesForTiles } from 'services/workflows/templates'


import { OwnerList } from './owner_list'
import { SplashScreen } from './splash_screen'
import { TrialBanner } from './trial_banner'
import { DisabledState as WorkflowsDisabledState } from './workflows_disabled_state'
import {
  fetchStatistics as fetchOwnerStatistics,
  personalWorkflowsCountSelector,
  teamWorkflowsCountSelector, workflowsOwnerStatisticsIsFailedSelector,
  workflowsOwnerStatisticsIsLoadedSelector
} from '../../owner_statistics'
import { DisabledTemplatesList } from '../../templates_list'
import { useLoadingState } from '../loading_state'

const Contents = () => {
  const isNewEmptyStateEnabled = useNewEmptyStates()
  const personalWorkflowsCount = useSelector(personalWorkflowsCountSelector)
  const teamWorkflowsCount = useSelector(teamWorkflowsCountSelector)
  const isOwnerOrAdmin = useSelector(isAdminOrOwnerSelector)
  const { admin_managed: adminManaged } = useQueryParams()

  const { data: policyData, isFetching } = useGetPolicyQuery()

  const currentUserCanUseWorkflows = !isFetching && policyData.canUseWorkflows

  if (teamWorkflowsCount > 0) {
    return <OwnerList />
  }

  if (!currentUserCanUseWorkflows) {
    return (
      <WorkflowsDisabledState
        ownerType="user"
        bannerCopy={I18n.t('publisher.workflows.contact_admin_to_enable.message_banner_html')}
      />
    )
  }

  if (personalWorkflowsCount === 0 && teamWorkflowsCount === 0 && !adminManaged) {
    return (
      <>
        {!isNewEmptyStateEnabled && <TrialBanner />}
        <SplashScreen
          owner={{
            type: 'user',
            id: 'me',
            admin: isOwnerOrAdmin
          }}
        />
      </>
    )
  }

  return <OwnerList />
}

export const MyCalendlyList = () => {
  const { isLoaded, setLoaded } = useLoadingState()
  const dispatch = useDispatch()
  const ownerListLoaded = useSelector(ownerListLoadedSelector)
  const workflowsOwnerStatisticsIsLoaded = useSelector(workflowsOwnerStatisticsIsLoadedSelector)
  const workflowsOwnerStatisticsIsFailed = useSelector(workflowsOwnerStatisticsIsFailedSelector)
  const workflowsExtractionEnabled = useFlag('raven_workflows_extraction')

  const { page = 1, admin_managed: adminManaged } = useQueryParams()
  const currentUserId = useSelector(idSelector)

  const upgradeIsRequired = useSelector((state) => isRestrictedFeatureSelector(state, { featureKey: 'workflows' }))
  const isOwnerOrAdmin = useSelector(isAdminOrOwnerSelector)
  const isNewEmptyStateEnabled = useNewEmptyStates()

  useEffect(() => {
    setLoaded(
      ownerListLoaded
        && (workflowsOwnerStatisticsIsLoaded || workflowsOwnerStatisticsIsFailed)
    )
  }, [ownerListLoaded, setLoaded, workflowsOwnerStatisticsIsFailed, workflowsOwnerStatisticsIsLoaded])

  useEffect(() => {
    dispatch(fetchOwnerStatistics({ userId: currentUserId, workflowsExtractionEnabled }))
  }, [currentUserId, dispatch, workflowsExtractionEnabled])

  useEffect(() => {
    dispatch(fetchMyCalendlyOwners({ page, adminManaged, workflowsExtractionEnabled }))
    return () => {
      dispatch({
        type: 'WORKFLOWS_CLEAR_CREATION_FLAG'
      })
    }
  }, [dispatch, page, adminManaged, workflowsExtractionEnabled])

  if (upgradeIsRequired) {
    if (isNewEmptyStateEnabled) {
      return (
        <EmptyState type="templates" contentKey="workflows">
          <DisabledTemplatesList
            owner={currentUserId}
            templateIds={getTemplatesForTiles()}
          />
        </EmptyState>
      )
    }
    const copy = isOwnerOrAdmin ? I18n.t('publisher.workflows.upgrade.message_banner_html')
      : I18n.t('publisher.workflows.contact_admin_for_upgrade.message_banner_html')

    return (<WorkflowsDisabledState bannerCopy={copy} />)
  }

  if (!isLoaded) {
    return null
  }

  return <Contents />
}
